import { DetailsDrawerState } from './types';
import {
  dispatchAction,
  persistentReducedStream,
  reducer,
} from '@ardoq/rxbeach';
import {
  hideDetailsDrawer,
  setDetailsDrawerBusinessCriticalFieldIds,
  setDrawerData,
} from './actions';
import { detailsDrawerOperations } from './detailsDrawerOperations';
import { combineLatest, map, debounceTime } from 'rxjs';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { appLayout$ } from '../../appLayout$';
import { featureContext$ } from 'streams/featureContext$';
import { featureOperations, Features } from '@ardoq/features';
import { getBusinessCriticalFieldNames } from '@ardoq/component-overview';
import { toOpenEditPriorityFieldsProps } from 'priorityFields/openEditPriorityFieldsProps';
import { i18nLocale$ } from 'streams/i18nLocale$';

export type DetailsDrawerCommands = {
  hideDetailsDrawer: VoidFunction;
};

const detailsDrawerCommands: DetailsDrawerCommands = {
  hideDetailsDrawer: () => dispatchAction(hideDetailsDrawer()),
};

const defaultState: DetailsDrawerState = {
  scopeData: null,
  componentId: null,
  businessCriticalFieldIds: null,
};

export const detailsDrawerState$ = persistentReducedStream<DetailsDrawerState>(
  'detailsDrawer$',
  defaultState,
  [
    reducer(setDrawerData, detailsDrawerOperations.setDrawerData),
    reducer(
      setDetailsDrawerBusinessCriticalFieldIds,
      detailsDrawerOperations.setBusinessCriticalFieldIds
    ),
  ]
);

export const detailsDrawer$ = combineLatest({
  detailsDrawerState: detailsDrawerState$,
  permissionContext: currentUserPermissionContext$,
  appLayoutState: appLayout$,
  featureContext: featureContext$,
  i18nLocale: i18nLocale$,
}).pipe(
  debounceTime(100),
  map(
    ({
      detailsDrawerState,
      permissionContext,
      appLayoutState,
      featureContext,
      i18nLocale,
    }) => {
      const { scopeData, componentId, businessCriticalFieldIds } =
        detailsDrawerState;
      const selectedComponent = componentId
        ? scopeData?.componentsById[componentId]
        : undefined;

      const hasDiscoverInCore = featureOperations.hasFeature(
        featureContext,
        Features.DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION
      );
      const priorityFieldNames =
        (hasDiscoverInCore &&
          businessCriticalFieldIds &&
          getBusinessCriticalFieldNames(
            scopeData,
            businessCriticalFieldIds,
            componentId
          )) ||
        [];

      const isAdmin = featureContext.isOrgAdmin;

      return {
        ...detailsDrawerState,
        scopeData,
        bottomOffsetHeight: appLayoutState.leftPaneStyle.bottom ?? 0,
        commands: detailsDrawerCommands,
        permissionContext,
        priorityFieldNames,
        hasDiscoverInCore,
        isAdmin,
        i18nLocale,
        openEditPriorityFieldsProps: toOpenEditPriorityFieldsProps({
          isAdmin,
          hasDiscoverInCore,
          hasPriorityFields: priorityFieldNames.length > 0,
          componentTypeName: selectedComponent?.type ?? '',
        }),
      };
    }
  )
);
